import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { t, currentLang } from "@/lib/i18n";
import { RefreshCw } from "lucide-react";

const levelDot = {
  info: "bg-slate-400",
  success: "bg-emerald-500",
  warn: "bg-amber-500",
  error: "bg-rose-500",
};

function relTime(iso) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  const en = currentLang === "en";
  if (diff < 60) {
    const s = Math.max(1, Math.round(diff));
    return en ? `${s}s ago` : `${s} с назад`;
  }
  if (diff < 3600) {
    const m = Math.round(diff / 60);
    return en ? `${m}m ago` : `${m} мин назад`;
  }
  if (diff < 86400) {
    const h = Math.round(diff / 3600);
    return en ? `${h}h ago` : `${h} ч назад`;
  }
  return new Date(iso).toLocaleDateString(en ? "en-US" : "ru-RU");
}

export default function LiveFeed({ height = 320 }) {
  const [items, setItems] = useState([]);
  const [accountsById, setAccountsById] = useState({});
  const [devicesById, setDevicesById] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [logs, accs, devs] = await Promise.all([
        api.logs({ limit: 25 }),
        accountsCache ? Promise.resolve(accountsCache) : api.accounts(),
        devicesCache ? Promise.resolve(devicesCache) : api.devices(),
      ]);
      accountsCache = accs;
      devicesCache = devs;
      setAccountsById(Object.fromEntries(accs.map((a) => [a.id, a])));
      setDevicesById(Object.fromEntries(devs.map((d) => [d.id, d])));
      setItems(logs);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const tm = setInterval(load, 5000);
    return () => clearInterval(tm);
  }, []);

  return (
    <div className="card-soft p-6 flex flex-col" style={{ height }}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-display font-semibold text-slate-900 flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5">
              <span className="pulse-dot text-emerald-500" />
              {t.dashboard.liveFeed}
            </span>
          </div>
          <div className="text-xs text-slate-500 mt-0.5">{t.dashboard.liveFeedSub}</div>
        </div>
        <RefreshCw
          className={`h-4 w-4 text-slate-300 ${loading ? "animate-spin" : ""}`}
        />
      </div>
      <div className="flex-1 overflow-y-auto space-y-1.5 pr-1 -mr-1">
        {items.length === 0 ? (
          <div className="text-sm text-slate-400 py-6 text-center">
            {t.dashboard.liveFeedEmpty}
          </div>
        ) : (
          items.map((l) => {
            const acc = accountsById[l.account_id];
            const dev = devicesById[l.device_id];
            return (
              <div key={l.id} className="flex items-start gap-2 text-xs py-1.5 border-b border-slate-100 last:border-0">
                <span className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${levelDot[l.level] || levelDot.info}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-medium text-slate-900">{l.action}</span>
                    {acc && <span className="text-slate-500">· {acc.username}</span>}
                    {dev && <span className="text-slate-500">· {dev.name}</span>}
                  </div>
                  {l.detail && <div className="text-slate-600 truncate mt-0.5">{l.detail}</div>}
                </div>
                <span className="text-[10px] text-slate-400 shrink-0 whitespace-nowrap">
                  {relTime(l.ts)}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// module-level cache to avoid re-fetching accounts/devices every 5 s
let accountsCache = null;
let devicesCache = null;
